import { environment } from 'environments/environment';

export type UserStationLike = Record<string, unknown>;

/** Code entreprise / station forcé (ex. 001 = Kastelo). Repli si aucune station ERP. */
export function getStaticEnterpriseCode(): string {
    const code = (environment as { static_code_entreprise?: string }).static_code_entreprise;
    return String(code ?? '001').trim();
}

/**
 * Code station logiciel caisse dérivé de l'id station ERP.
 * Ex. id 1 → « 001 », id 13 → « 013 », id 1000 → « 1000 ».
 */
export function formatStationIdAsCode(stationId: number): string {
    const n = Number(stationId);
    if (!Number.isFinite(n) || n <= 0) {
        return '';
    }
    return String(Math.trunc(n)).padStart(3, '0');
}

function getStationNumericId(station: UserStationLike | null | undefined): number {
    if (station == null) {
        return 0;
    }
    const n = Number(station['id'] ?? station['station_id']);
    if (!Number.isFinite(n) || n <= 0) {
        return 0;
    }
    return Math.floor(n);
}

/**
 * Station active ERP : connectée (`isConnecte`), sinon première station du user.
 */
export function pickStationFromUser(user: unknown): UserStationLike | null {
    if (user == null || typeof user !== 'object') {
        return null;
    }
    const u = user as Record<string, unknown>;
    const stations = Array.isArray(u['stations']) ? u['stations'] : [];

    const connected = stations.find(
        (s) =>
            s != null &&
            typeof s === 'object' &&
            ((s as UserStationLike)['isConnecte'] === 1 || (s as UserStationLike)['isConnecte'] === true)
    ) as UserStationLike | undefined;

    const picked = (connected ?? (stations[0] as UserStationLike | undefined)) ?? null;
    if (picked == null) {
        return { code_station: getStaticEnterpriseCode(), id: 1, station_id: 1 };
    }

    const stationId = getStationNumericId(picked);
    const code_station = stationId > 0 ? formatStationIdAsCode(stationId) : getStaticEnterpriseCode();
    return { ...picked, code_station };
}

/**
 * ID station ERP (`stations_user.id` dans back-erp).
 * Priorité : `id` (PK stations_user), puis `station_id` seulement s'il est cohérent.
 * Ne pas préférer un `station_id` logiciel-caisse éventuellement collé sur l'objet.
 */
export function getEnterpriseStationIdFromUser(user: unknown): number {
    const station = pickStationFromUser(user);
    if (station == null) {
        return 0;
    }
    const u = user != null && typeof user === 'object' ? (user as Record<string, unknown>) : null;
    // Toujours privilégier l'id stations_user ERP
    const fromId = Number(station['id']);
    if (Number.isFinite(fromId) && fromId > 0) {
        return Math.floor(fromId);
    }
    const fromStationId = Number(station['station_id'] ?? u?.['station_id']);
    if (Number.isFinite(fromStationId) && fromStationId > 0) {
        return Math.floor(fromStationId);
    }
    return 0;
}

export function getEnterpriseStationCodeFromUser(user: unknown): string {
    const stationId = getEnterpriseStationIdFromUser(user);
    if (stationId > 0) {
        return formatStationIdAsCode(stationId);
    }
    return getStaticEnterpriseCode();
}

export function getEnterpriseStationIdFromLocalStorage(): number {
    const raw = localStorage.getItem('currentUser');
    if (raw == null || raw === '') {
        return 0;
    }
    try {
        return getEnterpriseStationIdFromUser(JSON.parse(raw));
    } catch {
        return 0;
    }
}

/** Stations ERP du user connecté (localStorage). */
export function listUserStationsFromLocalStorage(): UserStationLike[] {
    const raw = localStorage.getItem('currentUser');
    if (raw == null || raw === '') {
        return [];
    }
    try {
        const user = JSON.parse(raw) as Record<string, unknown>;
        const stations = Array.isArray(user['stations']) ? (user['stations'] as UserStationLike[]) : [];
        return stations.filter((s) => getStationNumericId(s) > 0);
    } catch {
        return [];
    }
}

/**
 * Marque une station comme connectée (`isConnecte`) dans currentUser.
 * Si elle n’est pas encore dans `stations`, l’ajoute (sélection établissement).
 * Retourne l’id numérique effectif, ou 0 si échec.
 */
export function setConnectedEnterpriseStationId(
    stationId: number,
    meta?: { label?: string; nom_entreprise?: string; logo?: string | null }
): number {
    const target = Math.floor(Number(stationId));
    if (!Number.isFinite(target) || target <= 0) {
        return 0;
    }
    const raw = localStorage.getItem('currentUser');
    if (raw == null || raw === '') {
        return 0;
    }
    try {
        const user = JSON.parse(raw) as Record<string, unknown>;
        const stations = Array.isArray(user['stations']) ? (user['stations'] as UserStationLike[]) : [];
        let matched = false;
        const nextStations: UserStationLike[] = stations.map((s) => {
            const id = getStationNumericId(s);
            const isTarget = id === target;
            if (isTarget) {
                matched = true;
            }
            return { ...s, isConnecte: isTarget ? 1 : 0 } as UserStationLike;
        });
        if (!matched) {
            const added: UserStationLike = {
                id: target,
                station_id: target,
                isConnecte: 1,
                label: meta?.label || meta?.nom_entreprise || `Établissement ${target}`,
                nom_entreprise: meta?.nom_entreprise || meta?.label || `Établissement ${target}`,
            };
            if (meta?.logo) {
                added['logo'] = meta.logo;
            }
            nextStations.push(added);
        }
        localStorage.setItem(
            'currentUser',
            JSON.stringify({ ...user, station_id: target, stations: nextStations })
        );
        return target;
    } catch {
        return 0;
    }
}

/** Libellé affiché si aucune entreprise n'est résolue (ex. avant connexion). */
export const DEFAULT_ENTERPRISE_DISPLAY_NAME = 'Entreprise';

/** Nom affichable à partir d'un objet station / entreprise. */
export function getEnterpriseNameFromStation(station: UserStationLike | null | undefined): string {
    if (station == null) {
        return '';
    }
    return String(
        station['nom_entreprise'] ??
            station['label'] ??
            station['nom'] ??
            station['raison_sociale'] ??
            station['nom_station'] ??
            ''
    ).trim();
}

/** Nom de l'entreprise connectée à partir de l'utilisateur (localStorage / session). */
export function getConnectedEnterpriseNameFromUser(user: unknown): string {
    const fromPicked = getEnterpriseNameFromStation(pickStationFromUser(user));
    if (fromPicked) {
        return fromPicked;
    }
    if (user == null || typeof user !== 'object') {
        return '';
    }
    const stations = Array.isArray((user as Record<string, unknown>)['stations'])
        ? ((user as Record<string, unknown>)['stations'] as UserStationLike[])
        : [];
    const connected = stations.find(
        (s) => s != null && (s['isConnecte'] === 1 || s['isConnecte'] === true)
    );
    return getEnterpriseNameFromStation(connected ?? stations[0]);
}

export function getConnectedEnterpriseNameFromLocalStorage(): string {
    const raw = localStorage.getItem('currentUser');
    if (raw == null || raw === '') {
        return '';
    }
    try {
        return getConnectedEnterpriseNameFromUser(JSON.parse(raw));
    } catch {
        return '';
    }
}

/** Valeur affichée : candidat explicite, sinon entreprise connectée, sinon libellé par défaut. */
export function resolveEnterpriseDisplayName(candidate?: string | null): string {
    const trimmed = String(candidate ?? '').trim();
    if (trimmed) {
        return trimmed;
    }
    return getConnectedEnterpriseNameFromLocalStorage() || DEFAULT_ENTERPRISE_DISPLAY_NAME;
}
